import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Zap } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col md:flex-row">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1558980664-10e7170b5df9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMG1vdG9yY3ljbGV8ZW58MHx8fHwxNzg2NjMzNTI3fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-black/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <span className="w-9 h-9 rounded-lg bg-[#00FF66] flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" strokeWidth={2.5} />
            </span>
            <div className="font-display font-black">SHRI SHANTESHWAR</div>
          </Link>
          <h2 className="font-display font-black text-4xl leading-tight">Welcome back to the SSE Referral Program.</h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-md bg-[#111111] border-white/10 p-8">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-2">Log in</div>
            <h1 className="font-display font-black text-3xl">Refer. Track. Cash out.</h1>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-white/70 text-xs uppercase tracking-[0.2em]">Email</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-2 bg-black/40 border-white/10 text-white h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/70 text-xs uppercase tracking-[0.2em]">Password</Label>
              <Input
                id="password" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-2 bg-black/40 border-white/10 text-white h-11"
              />
            </div>
            <Button
              type="submit" disabled={busy}
              className="w-full rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold h-11"
              data-testid="login-submit"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-white/50">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#00FF66] hover:underline" data-testid="login-to-register">
              Join the program
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
